// P1-008: Agent Deployer — formation validation and compute cost checking
import { useAgentStore } from '../stores/agentStore';
import { useHardwareStore } from '../stores/hardwareStore';
import type { OwnedAgent } from '../stores/agentStore';

export interface DeploymentValidation {
  valid: boolean;
  errors: string[];
}

export interface TeamComputeInfo {
  totalComputeCost: number;
  totalCompute: number;
  canDeploy: boolean;
}

export const AgentDeployer = {
  /**
   * Validate that the current formation is legal:
   * - Exactly 5 agents
   * - No duplicate agents
   */
  validateFormation(formation?: (OwnedAgent | null)[]): DeploymentValidation {
    const team = formation ?? useAgentStore.getState().getFormation();
    const errors: string[] = [];

    // Check exactly 5 agents
    const deployed = team.filter((a): a is OwnedAgent => a !== null);
    if (deployed.length !== 5) {
      errors.push(`Need exactly 5 agents, currently have ${deployed.length}`);
    }

    // Check no duplicates
    const ids = new Set<string>();
    for (const agent of deployed) {
      if (ids.has(agent.instanceId)) {
        errors.push(`Duplicate agent: ${agent.name} (${agent.instanceId})`);
      }
      ids.add(agent.instanceId);
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  },

  /**
   * Calculate the total compute cost of the current formation.
   */
  calculateTeamComputeCost(formation?: (OwnedAgent | null)[]): number {
    const team = formation ?? useAgentStore.getState().getFormation();
    return team.reduce((sum, agent) => sum + (agent?.computeCost ?? 0), 0);
  },

  /**
   * Check if current formation can be deployed (compute cost <= total compute).
   */
  canDeploy(formation?: (OwnedAgent | null)[]): TeamComputeInfo {
    const totalComputeCost = AgentDeployer.calculateTeamComputeCost(formation);
    const totalCompute = useHardwareStore.getState().getTotalCompute();

    return {
      totalComputeCost,
      totalCompute,
      canDeploy: totalComputeCost <= totalCompute,
    };
  },

  /**
   * Full deployment check: validate formation + check compute.
   * Returns combined errors.
   */
  fullCheck(formation?: (OwnedAgent | null)[]): DeploymentValidation {
    const validation = AgentDeployer.validateFormation(formation);
    const computeInfo = AgentDeployer.canDeploy(formation);

    if (!computeInfo.canDeploy) {
      validation.valid = false;
      validation.errors.push(
        `Compute cost (${computeInfo.totalComputeCost}) exceeds available compute (${computeInfo.totalCompute})`
      );
    }

    return validation;
  },
};
